import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';

// fonts
import 'assets/fonts/inter/inter.css';

// global brand tokens (MCC)
import 'brand/brand.css';
import 'styles/shadcn.css';

// scroll bar
import 'simplebar/dist/simplebar.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// google-fonts
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/700.css';

import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

import '@fontsource/public-sans/400.css';
import '@fontsource/public-sans/500.css';
import '@fontsource/public-sans/600.css';
import '@fontsource/public-sans/700.css';

// project-imports
import App from './App';
import { ConfigProvider } from 'contexts/ConfigContext';
import reportWebVitals from './reportWebVitals';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN || '';
const sentryEnvironment =
  import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development';
const sentryRelease = import.meta.env.VITE_RELEASE || undefined;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    release: sentryRelease,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.05),
    replaysSessionSampleRate: Number(
      import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE || 0.0
    ),
    replaysOnErrorSampleRate: Number(
      import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE || 1.0
    ),
    beforeSend(event, hint) {
      const message = hint?.originalException?.message || event?.message || '';
      const ignoredPatterns = [
        /ResizeObserver loop limit exceeded/i,
        /NetworkError/i,
        /Load failed/i,
        /AbortError/i,
        /Non-Error promise rejection captured/i,
      ];

      if (ignoredPatterns.some((pattern) => pattern.test(String(message)))) {
        return null;
      }

      return event;
    },
  });
}

// ==============================|| CHUNK LOAD ERROR HANDLER ||============================== //
// Auto-refresh when cached JS tries to load chunks that no longer exist after a rebuild

const handleChunkLoadError = (error) => {
  const chunkFailedMessage = /Loading chunk [\d]+ failed|Failed to fetch dynamically imported module/;

  if (chunkFailedMessage.test(error.message)) {
    // Prevent infinite refresh loop - only refresh once per session
    const lastRefresh = sessionStorage.getItem('chunk_error_refresh');
    const now = Date.now();

    // Only auto-refresh if we haven't refreshed in the last 10 seconds
    if (!lastRefresh || now - parseInt(lastRefresh, 10) > 10000) {
      sessionStorage.setItem('chunk_error_refresh', now.toString());
      console.warn('Chunk load error detected, refreshing page to get latest version...');
      window.location.reload();
      return true;
    }
  }
  return false;
};

// Global error handler for uncaught chunk loading errors
window.addEventListener('error', (event) => {
  if (handleChunkLoadError(event.error || { message: event.message })) {
    event.preventDefault();
  }
});

// Handle unhandled promise rejections (dynamic imports fail as rejected promises)
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && handleChunkLoadError(event.reason)) {
    event.preventDefault();
  }
});

const container = document.getElementById('root');
const root = createRoot(container);

// ==============================|| MAIN - REACT DOM RENDER  ||============================== //

root.render(
  <ConfigProvider>
    <App />
  </ConfigProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
